import { RegistryCharacter, RegistrySceneProp, RegistryAct, RegistryEpisodeSummary } from '../types';

/**
 * Normalize characters from two possible TOS shapes:
 * 1. { characters: [...] } — frontend envelope
 * 2. { name: Character, ... } — legacy pipeline map keyed by character name
 */
export function normalizeCharacters(raw: unknown): RegistryCharacter[] {
  if (!raw || typeof raw !== 'object') return [];
  const obj = raw as Record<string, unknown>;

  if (Array.isArray(obj.characters)) {
    return obj.characters as RegistryCharacter[];
  }

  if (Array.isArray(raw)) {
    return raw as RegistryCharacter[];
  }

  const entries = Object.entries(obj);
  if (entries.length === 0) return [];

  return entries.map(([name, value]) => {
    const char = value as Record<string, unknown>;
    return {
      name,
      aliases: (char.aliases as string[]) ?? [],
      bio: (char.bio as string) ?? '',
      appearance: (char.appearance as string) ?? '',
      tags: (char.tags as string[]) ?? [],
      relationships: (char.relationships as Record<string, string>) ?? {},
      state_timeline: (char.state_timeline as RegistryCharacter['state_timeline']) ?? [],
      appeared_in_episodes: (char.appeared_in_episodes as number[]) ?? [],
    };
  });
}

/**
 * Normalize scene props from two possible TOS shapes:
 * 1. { items: [...] } — frontend envelope
 * 2. { scenes: { name: Scene }, props: { name: Prop } } — legacy pipeline map
 */
export function normalizeSceneProps(raw: unknown): RegistrySceneProp[] {
  if (!raw || typeof raw !== 'object') return [];
  const obj = raw as Record<string, unknown>;

  if (Array.isArray(obj.items)) {
    return obj.items as RegistrySceneProp[];
  }

  if (Array.isArray(raw)) {
    return raw as RegistrySceneProp[];
  }

  const items: RegistrySceneProp[] = [];

  const scenes = obj.scenes as Record<string, Record<string, unknown>> | undefined;
  if (scenes && typeof scenes === 'object') {
    for (const [name, value] of Object.entries(scenes)) {
      items.push({
        name,
        type: 'scene',
        description: (value.description as string) ?? '',
        tags: (value.tags as string[]) ?? [],
        appeared_in_episodes: (value.appeared_in_episodes as number[]) ?? [],
      });
    }
  }

  const props = obj.props as Record<string, Record<string, unknown>> | undefined;
  if (props && typeof props === 'object') {
    for (const [name, value] of Object.entries(props)) {
      items.push({
        name,
        type: 'prop',
        description: (value.description as string) ?? '',
        tags: (value.tags as string[]) ?? [],
        appeared_in_episodes: (value.appeared_in_episodes as number[]) ?? [],
      });
    }
  }

  return items;
}

/**
 * Normalize acts from two possible TOS shapes:
 * 1. bare array: [{ act_id, ... }]
 * 2. { acts: [...] } — envelope
 */
export function normalizeActs(raw: unknown): RegistryAct[] {
  if (Array.isArray(raw)) {
    return raw as RegistryAct[];
  }
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.acts)) {
      return obj.acts as RegistryAct[];
    }
  }
  return [];
}

/**
 * Normalize raw episode summaries from TOS (bare array or { episodes: [...] }).
 */
function normalizeRawEpisodes(raw: unknown): Array<{ episode: number; summary: string }> {
  if (Array.isArray(raw)) {
    return raw as Array<{ episode: number; summary: string }>;
  }
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.episodes)) {
      return obj.episodes as Array<{ episode: number; summary: string }>;
    }
  }
  return [];
}

/**
 * Derive key_characters and key_scenes for each episode by cross-referencing
 * character and scene-prop registries' appeared_in_episodes fields.
 */
export function enrichEpisodes(
  rawEpisodes: unknown,
  characters: RegistryCharacter[],
  sceneProps: RegistrySceneProp[],
): RegistryEpisodeSummary[] {
  const episodes = normalizeRawEpisodes(rawEpisodes);

  const charsByEp = new Map<number, string[]>();
  for (const char of characters) {
    for (const ep of char.appeared_in_episodes ?? []) {
      const existing = charsByEp.get(ep) ?? [];
      existing.push(char.name);
      charsByEp.set(ep, existing);
    }
  }

  const scenesByEp = new Map<number, string[]>();
  for (const sp of sceneProps) {
    for (const ep of sp.appeared_in_episodes ?? []) {
      const existing = scenesByEp.get(ep) ?? [];
      existing.push(sp.name);
      scenesByEp.set(ep, existing);
    }
  }

  return episodes.map((ep) => ({
    episode: ep.episode,
    summary: ep.summary,
    key_characters: charsByEp.get(ep.episode) ?? [],
    key_scenes: scenesByEp.get(ep.episode) ?? [],
  }));
}
